import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { firstValueFrom, take } from 'rxjs';

import { FeedApiService } from 'src/app/api/services/feed-api.service';
import { injectFeedWriteRefresh } from 'src/app/api/queries/feed.queries';
import { UtilsDataObjectApiService } from 'src/app/api/services/utils-data-object-api.service';
import { ToastService } from '@services/toast.service';

import * as feedActions from '../actions/feed.actions';
import { submitFormFailure, submitFormSuccess } from '../actions/form.actions';
import { DimensionAttachTypeId, DimensionJoinTypeId } from '../constants';
import { DimensionAttachParams } from './feed.service';
import { EditorFormEntity } from '../models/form.models';
import { selectFeed } from '../reducers';

/**
 * A feed's attached dimensions: attach, detach, flip join type, and
 * create-and-attach.
 *
 * A root service because one of its two consumers **is** a root service:
 * `breadcrumb.service` owns the dimension chips and their dropdown, and
 * `query-editor-dialog` owns create-and-attach.
 *
 * **Every method broadcasts its `…Success` action** for the client state -
 * `feed.reducer` rewrites `feed.dimensions` + `feed.query` off them, and the
 * query editor closes itself on `feedAttachDimensionsSuccess` - and then runs
 * the content refresh itself: posts, widget data and expanded filters all
 * answer differently after a dimension write.
 */
@Injectable({ providedIn: 'root' })
export class FeedDimensionsService {
  private readonly store = inject(Store);
  private readonly feedWriteRefresh = injectFeedWriteRefresh();
  private readonly feedApi = inject(FeedApiService);
  private readonly utilsDataObjectApi = inject(UtilsDataObjectApiService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  /** Replace the feed's attached-dimension set with `dimensionIds`. */
  async attach(
    feedId: string,
    dimensionIds: string[],
    attachTypeId: DimensionAttachTypeId,
  ): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.utilsDataObjectApi.attachDimensions(
          feedId,
          dimensionIds,
          attachTypeId,
        ),
      );

      this.broadcastAttached(response.dimensions, response.query);
    } catch {
      // * silent by design
    }
  }

  async detach(feedId: string, dimensionId: string): Promise<void> {
    try {
      const { query } = await firstValueFrom(
        this.utilsDataObjectApi.detachDimension(feedId, dimensionId),
      );

      this.store.dispatch(
        feedActions.feedDetachDimensionSuccess({ dimensionId, query }),
      );
      this.feedWriteRefresh();
    } catch {
      // * silent by design
    }
  }

  async setJoinType(
    feedId: string,
    dimensionId: string,
    joinTypeId: DimensionJoinTypeId,
  ): Promise<void> {
    try {
      const { query } = await firstValueFrom(
        this.utilsDataObjectApi.setDimensionJoinType(
          feedId,
          dimensionId,
          joinTypeId,
        ),
      );

      this.store.dispatch(
        feedActions.setDimensionJoinTypeSuccess({
          dimensionId,
          joinTypeId,
          query,
        }),
      );
      this.feedWriteRefresh();
    } catch {
      // * silent by design
    }
  }

  /**
   * Two requests, one user action: create a dimension, then attach it alongside
   * the ones the feed already has.
   *
   * **No query validation on this path**, unlike the feed create chain.
   */
  async createAndAttach(
    dimensionData: any,
    attachParams: DimensionAttachParams,
  ): Promise<void> {
    const feed = await firstValueFrom(
      this.store.select(selectFeed).pipe(take(1)),
    );
    const currentDimensions = (feed?.dimensions ?? []).map(
      (dimension) => dimension.id,
    );

    let dimension;
    try {
      dimension = await firstValueFrom(this.feedApi.createFeed(dimensionData));
    } catch {
      this.store.dispatch(
        submitFormFailure({
          entity: EditorFormEntity.Feed,
          error: 'Failed to create dimension',
        }),
      );
      return;
    }

    try {
      const response = await firstValueFrom(
        this.utilsDataObjectApi.attachDimensions(
          attachParams.object_id,
          [...currentDimensions, dimension.id],
          attachParams.attach_type_id,
        ),
      );

      this.store.dispatch(feedActions.createAndAttachDimensionSuccess());
      this.broadcastAttached(response.dimensions, response.query);
      this.store.dispatch(
        submitFormSuccess({
          entity: EditorFormEntity.Feed,
          object: dimension,
          isCreated: true,
        }),
      );
    } catch {
      this.store.dispatch(
        submitFormFailure({
          entity: EditorFormEntity.Feed,
          error: 'Failed to attach dimension',
        }),
      );
    }
  }

  /** Both attach paths broadcast, refresh and toast through here. */
  private broadcastAttached(dimensions: any[], query: string): void {
    this.store.dispatch(
      feedActions.feedAttachDimensionsSuccess({ dimensions, query }),
    );
    this.feedWriteRefresh();
    this.toastService.showSuccess(
      this.translateService.instant('feed.toasts.attached-dimensions-updated'),
    );
  }
}
